import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete,Button,Dialog,DialogActions,DialogContent,DialogTitle,Divider,FormControl,IconButton,InputLabel,MenuItem,Select,Stack,TextField,Typography } from "@mui/material";
import { DesktopDateTimePicker,LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChamadoChecklist,modeloType } from "../../../types/new-form-filling/types";
import { UserContext } from '../../../context/UserContext';
import { WebFormContext } from '../../../context/WebFormContext';

export default function OpenTicketModal({
  openModal,
  setOpenModal,
  modelos,
  chamados,
  limite,
  setChamados,
  setSelectedOption }: {
    openModal: boolean,
    setOpenModal: Function,
    modelos: modeloType[] | undefined,
    chamados: ChamadoChecklist[] | [],
    setChamados: Function
    limite: number | undefined,
    setSelectedOption?: Function
  }) {
  const { t } = useTranslation();
  const { selectedConjunto } = useContext(UserContext);
  const { webForm } = useContext(WebFormContext);
  const conjuntoId = selectedConjunto?.id ?? webForm.conjunto;

  type AutocompleteTicketOption = {
    selKey: string;
    selecionado: {
      id: string;
      nome: string;
      message: string;
    };
  };

  const defaultChamado: ChamadoChecklist = { selKey: '', selecionado: { id: '', nome: 'Selecine um modelo', message: '' }, textChamado: '', dateChamado: new Date(Date.now() + 24 * 60 * 60 * 1000).setHours(23, 55, 59), prioridade: 'baixa' };

  const [chamadosToSave, setChamadosToSave] = useState<ChamadoChecklist[]>(chamados.length > 0 ? chamados : [defaultChamado]);

  function checkDisabled() {
    if (!chamadosToSave?.length || chamadosToSave?.some((ele: ChamadoChecklist) => !ele?.selecionado?.id || !ele?.dateChamado || !ele?.prioridade || !ele?.textChamado)) {
      return true;
    }
    return false;
  }

  const modelosFiltrados = useMemo(() => {
    if (!conjuntoId) return [];
    return modelos
      ?.filter((modelo) => modelo.setsList?.includes(conjuntoId))
      ?.sort((a, b) => a.nome?.localeCompare(b.nome))
      ?.map((ticket) => ({
        selKey: ticket.id,
        selecionado: {
          id: ticket.id,
          nome: ticket.nome,
          message: ticket.message || '',
        }
      })) || [];
  }, [modelos, conjuntoId]);

  const noOptionsText = conjuntoId
    ? t('formFilling.selectSetToSeeModels')
    : !modelos
      ? t('formFilling.loadingModels')
      : modelos.length === 0
        ? t('formFilling.noModelsAvailable')
        : modelosFiltrados.length === 0
          ? t('formFilling.noModelsForSet')
          : t('formFilling.noModelsFound');

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={openModal}
      onClose={() => { setOpenModal(false); setSelectedOption && setSelectedOption(''); }}
      scroll={'paper'}
    >
      <DialogTitle id="scroll-dialog-title">{t('formFilling.openTicket')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => { setOpenModal(false); setSelectedOption && setSelectedOption(''); }}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="stretch"
          spacing={2}
        >
          {chamadosToSave?.map((ticket: ChamadoChecklist, index: number) => {
            return (
              <div key={`index${index}|id${ticket?.selKey}`}>
                <Stack
                  direction="column"
                  spacing={2}
                  sx={{
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  <Autocomplete<AutocompleteTicketOption, false, false, false>
                    fullWidth
                    noOptionsText={noOptionsText}
                    loading={!modelos}
                    loadingText={t('formFilling.loadingModels')}
                    getOptionLabel={(option) => option.selecionado?.nome || ''}
                    size="small"
                    value={ticket as unknown as AutocompleteTicketOption}
                    options={modelosFiltrados}
                    onChange={(_event, ticket: {
                      selKey: string,
                      selecionado: {
                        id: string,
                        nome: string,
                        message: string,
                      }
                    } | null) => {
                      if (ticket) {
                        setChamadosToSave((prev: ChamadoChecklist[]) => {
                          const previous = [...prev];
                          previous[index] = { ...previous[index], ...ticket };
                          return previous;
                        });
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.selKey}>
                        <span>{option.selecionado.nome}</span>
                      </li>)}
                    renderInput={(params) => <TextField {...params} label={t('formFilling.selectModel')} />}
                  />
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: '100%',
                    }}
                  >
                    <Typography variant="h6" color='textDisabled'>{ticket?.selecionado?.nome}</Typography>
                    <IconButton color="error" onClick={() => {
                      setChamadosToSave(chamadosToSave?.filter((_, i) => i !== index));
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={4}
                    label={t('Reports.modalDetailP.callInstructions')}
                    value={ticket?.textChamado}
                    onChange={(event) => {
                      setChamadosToSave((prev: ChamadoChecklist[]) => { ticket.textChamado = event.target.value; return [...prev]; });
                    }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={.5}
                  >
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                      <DesktopDateTimePicker
                        slotProps={{
                          textField: {
                            required: true,
                          },
                          actionBar: {
                            actions: ['clear'],
                          },
                          field: {
                            readOnly: true,
                          },
                        }}
                        sx={{ width: 350 }}
                        localeText={{
                          clearButtonLabel: 'Limpar',
                        }}
                        minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                        format='dd/MM/yyyy'
                        label={t('Reports.modalDetailP.callDeadline')}
                        value={ticket?.dateChamado as Date ?? new Date(Date.now() + 24 * 60 * 60 * 1000).setHours(23, 55, 59)}
                        onChange={(data) => {
                          setChamadosToSave((prev: ChamadoChecklist[]) => { ticket.dateChamado = data ? data.getTime() : ''; return [...prev]; });
                        }}
                      />
                    </LocalizationProvider>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">{t('Reports.modalDetailP.priority')}</InputLabel>
                      <Select
                        required
                        label={t('Reports.modalDetailP.priority')}
                        value={ticket?.prioridade}
                        onChange={(event) => {
                          setChamadosToSave((prev: ChamadoChecklist[]) => { ticket.prioridade = event.target.value; return [...prev]; });
                        }}
                      >
                        <MenuItem value="alta">{t('Reports.modalDetailP.high')}</MenuItem>
                        <MenuItem value="media">{t('Reports.modalDetailP.medium')}</MenuItem>
                        <MenuItem value="baixa">{t('Reports.modalDetailP.low')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Divider variant="middle" />
                </Stack>
                <Divider variant="middle" />
              </div>
            );
          })}
          <Button variant="contained" disabled={(chamadosToSave?.length === limite) && limite !== 0}
            onClick={() => setChamadosToSave((prev: ChamadoChecklist[]) => [...prev, defaultChamado])}>
            {t('notificationsAndTickets.addTicket')}
          </Button>
          {typeof limite === 'number' && limite > 0 && <Typography color='textDisabled' textAlign='center'>{`${limite as number - chamadosToSave?.length > 0
            ? t('formFilling.ucanopen') + " " + (limite as number - chamadosToSave?.length) + " " + t('formFilling.tickets')
            : t('formFilling.reachLimit')}`}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={checkDisabled()}
          onClick={() => {
            setChamados([...chamadosToSave]);
            setSelectedOption && setSelectedOption("");
            setOpenModal(false);

          }}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
